/* eslint-disable no-console */
import { SIGNUP_SCREEN } from "./types";

import { ChangeEvent, useCallback, useEffect, useState } from "react";
import { useRecoilState } from "recoil";

import { shallowCopy } from "utils";

import {
  CreateUserRespState,
  CurrentMethodState,
  IsTermAndConditionCheckedState,
  SignUpCurrentModalState,
  SignUpFormInputsState,
  SignUpScreenState,
} from "./state";
import { useNetwork, useWebSocket, useInstance, useWebAuthn } from "hooks";
import { API_URL } from "constants/api-url";
import { ENVS } from "constants/common";
import GlobalInstanceManager from "instance-manger";
// import { IFormInput } from "global-states";
import { ERRORS, ErrorTypes } from "constants/errors";
import { useSignin } from "views/signin";

let isSignupCompleted = false;
let popupRef: Window | null = null;
// Custom hook for managing signup form state and logic
export const useSignUp = () => {
  const [loading, setLoading] = useState(false);
  // State to manage the current step in the sign-up process
  const [currentStep, setCurrentStep] = useRecoilState(SignUpScreenState);
  const [currentModal, setCurrentModal] = useRecoilState(
    SignUpCurrentModalState
  );

  const { manageDefaultUser: manageSigninUser } = useSignin();

  // State for holding form inputs
  const [form, setForm] = useRecoilState(SignUpFormInputsState);
  // State for managing whether terms and conditions checkbox is checked
  const [checked, setChecked] = useRecoilState(IsTermAndConditionCheckedState);

  // State for Create User Resp
  const [userResp, setUserResp] = useRecoilState(CreateUserRespState);

  const [currentMethod, setCurrentMethod] = useRecoilState(CurrentMethodState);

  const { create } = useWebAuthn();
  //Hooks for instance
  const { instanceDetails, env } = useInstance();

  const { get: socialAuth } = useNetwork();

  //Hooks for websocket
  const { sendMessage, getEvent } = useWebSocket();

  const { onErrorEvent, onSuccessEvent, onStepChangeEvent, webcomponentProps } =
    GlobalInstanceManager;

  const {
    instance_id,
    auto_navigate,
    microsoft_redirect_url,
    facebook_redirect_url,
    apple_redirect_url,
    google_redirect_url,
  } = webcomponentProps;

  const handleSSO = async (param: any) => {
    if (param.errorCode) {
      onErrorEvent({ ...param, type: "sso-data" });
      if (typeof popupRef?.close === "function") {
        try {
          popupRef.close();
        } catch (error) {
          // eslint-disable-next-line no-console
          console.log({ error });
        }
      }
      if (param.errorCode === "EMAIL_EXISTS" && auto_navigate) {
        await manageSigninUser({ email: param?.email ?? "" });
      } else {
        onErrorEvent(ERRORS[param.errorCode as keyof ErrorTypes]);
      }
    } else if (Object.keys(param).length > 0 && param.userId) {
      onStepChangeEvent({ ...param, type: "sso-data" });
      const data = [];

      // Create a shallow copy of the form to avoid direct mutation
      const newArr = shallowCopy(form);

      // Iterate over the form array and update values based on the param data
      for (let i = 0; i < newArr.length; i++) {
        if (param[newArr[i].name]) {
          const payload = {
            ...newArr[i],
            value: param[newArr[i].name], // Update the value from param
            disabled: true, // Disable the input
          };
          data.push(payload);
        } else {
          data.push(newArr[i]);
        }
      }
      // Update form state with the new data array
      setForm(data);

      const email = { id: param.email, isVerified: true } as any;
      // Set user response with userId from param
      setUserResp({ ...userResp, _id: param.userId, email });
      sendMessage("signUp", { userId: param.userId });

      // Set the current step in the signup process
      setCurrentStep(SIGNUP_SCREEN.ACCOUNT_DETAILS);

      // Close the popup if the close function is available
      if (typeof popupRef?.close === "function") {
        try {
          popupRef.close();
        } catch (error) {
          console.log({ error });
        }
      }
    }
  };
  /**
   * useEffect hook to handle incoming messages and update form state accordingly.
   */
  useEffect(() => {
    // Message event listener function
    const messageListener = async (event: MessageEvent) => {
      handleSSO(event.data);
    };

    // Add event listener for messages
    window.addEventListener("message", messageListener);

    // Cleanup function to remove event listener on component unmount
    return () => {
      window.removeEventListener("message", messageListener);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //hooks for submit credential
  const { post: submitCredential, loading: loadingSubmitCredential } =
    useNetwork();

  //hooks for submit credential
  const { post: resend, loading: loadingResend } = useNetwork();

  const { patch: submitSessionSuccess } = useNetwork();
  //hooks for submitRegistration credential
  const { patch: submitReg, loading: loadingRegistration } = useNetwork();

  const { post: getBiometricDetails } = useNetwork();

  const getWebAuthnOptions = useCallback(async () => {
    const resp = await getBiometricDetails(API_URL.PASSKEY, {
      type: "registrationOpts",
      payload: {
        rpId: window.location.origin,
        rpName: "Domain",
        userId: userResp._id,
      },
    });

    if (resp.statusCode === 200) {
      resp.registrationOptions.authenticatorSelection = {
        residentKey: "preferred",
        userVerification: "preferred",
        requireResidentKey: false,
      };
      onStepChangeEvent({
        ...resp,
        type: "webAuthn",
        from: "signup",
      });
      const response: any = await create(resp.registrationOptions);

      if (!response.isError) {
        await getBiometricDetails(API_URL.PASSKEY, {
          type: "verifyRegistration",
          payload: {
            registrationOptResponse: {
              ...response,
            },
            userId: userResp._id,
          },
        });

        return { apiResp: resp, webauthnResp: response };
      }
    } else {
      onErrorEvent(resp);
      return resp;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getBiometricDetails, userResp._id]);

  const handleOnCompleteSignUpProcess = useCallback(
    async (sessionId?: string) => {
      if (instanceDetails.methods?.biometric) {
        await getWebAuthnOptions();
      }

      if (userResp._id && sessionId) {
        await submitSessionSuccess(`${API_URL.USER}/${userResp._id}`, {
          sessionId,
        });
      }
    },
    [
      getWebAuthnOptions,
      instanceDetails.methods?.biometric,
      submitSessionSuccess,
      userResp,
    ]
  );

  /**
   * Handles changes in form inputs
   * @param {ChangeEvent<HTMLInputElement>} event - Change event from form input
   */
  const handleChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target ?? {}; // Extract value from input event
      const { index, validation, formatter } = event as any; // Extract index and validation function from event
      let inputVal = "";
      let validationInput = "";

      if (formatter) {
        const phoneObj = form.find((el) => el.name === "phone");

        const newData = formatter({
          value,
          countryCode: phoneObj?.countryCode ?? "+1",
        });
        inputVal = newData.formatted ?? value;
        validationInput = newData.validationInput ?? value;
      } else {
        inputVal = value;
        validationInput = value;
      }

      setForm(() => {
        const newObj = shallowCopy(form) ?? form; // Create a shallow copy of the form state
        if (newObj[index]) {
          // Check if the form element at index exists
          newObj[index]["value"] = inputVal; // Update the value of the form element
          if (validation) {
            // If validation function is provided
            const { isError, errorMessage } = validation(validationInput); // Validate the input value

            newObj[index]["isError"] = isError; // Set error status
            newObj[index]["errorMessage"] = errorMessage; // Set error message
          }
        }
        return newObj; // Return updated form state
      });
    },
    [form, setForm] // Dependency array to ensure the callback updates when form changes
  );

  /**
   * Handles changes in country code for form inputs
   * @param {string} value - Country code value
   * @param {number} index - Index of the form element to update
   */
  const handleChangeCountry = useCallback(
    (value: string, index: number) => {
      setForm(() => {
        const newObj = shallowCopy(form) ?? form; // Create a shallow copy of the form state
        if (newObj[index]) {
          // Check if the form element at index exists
          newObj[index].countryCode = value; // Update the country code of the form element
        }
        return newObj; // Return updated form state
      });
    },
    [form, setForm] // Dependency array to ensure the callback updates when form changes
  );

  /**
   * Placeholder function for submitting credentials (currently not implemented)
   * @returns {string} - Empty string (placeholder)
   */
  const submitCred = async (
    onSuccess?: (e: any) => void,
    onReject?: (e: any) => void,
    newSignForm?: any
  ): Promise<any> => {
    const userInfo: any = {}; // Object to hold user information
    const updatedForm = JSON.parse(JSON.stringify(newSignForm || form));

    // Check if form has elements
    for (let i = 0; i < updatedForm?.length; i++) {
      // Iterate over form elements
      const { name, value, countryCode, isCountryCodeVisible } =
        updatedForm[i] ?? {}; // Extract name and value from form element
      if (name && value) {
        if (isCountryCodeVisible) {
          Object.assign(userInfo, { ["countryCode"]: countryCode });
        }

        // Check if name and value are present
        Object.assign(userInfo, { [name]: value }); // Add name-value pair to userInfo
      }
    }

    const payload = {
      email: userInfo.email || undefined,
      instanceId: instance_id ?? "",
      phone: userInfo.phone?.replace(/\D/g, "") || undefined,
      countryCode: userInfo.countryCode || undefined,
    };

    setCurrentMethod(payload?.email ? "email" : "phone");
    const resp = await submitCredential(API_URL.USER, payload);

    if (resp?.statusCode !== 200) {
      if (
        resp.message === "User already exist" &&
        webcomponentProps.auto_navigate
      ) {
        await manageSigninUser(payload);
      }
      onErrorEvent({ ...resp, payload });
      onReject?.({ ...resp, data: payload });
    } else {
      setUserResp(resp);
      setForm((prev) => {
        const newObj = shallowCopy(prev);
        return newObj.map((el: any) => {
          if (instanceDetails.signUp?.scope.includes(el.name)) {
            return {
              ...el,
              disabled: true,
            };
          }
          return el;
        });
      });
      sendMessage("signUp", { userId: resp._id });
      onSuccess?.({ ...resp, data: payload });
      onStepChangeEvent({
        data: {
          ...resp,
          ...payload,
        },
        type: "cred",
      });
    }
  };

  const manageDefaultUser = async (user: any, param?: any) => {
    if (param) {
      handleSSO(param);
      return;
    }
    if (Object.keys(user || {}).length === 0) {
      return;
    }
    const newSignForm = form.map((el) => {
      return {
        ...el,
        value: user[el.name],
        ...(el.name === "phone" && {
          countryCode: user.countryCode || el.countryCode,
        }),
      };
    });
    setForm(newSignForm);
    await submitCred(
      () => {
        setCurrentModal(SIGNUP_SCREEN.VERIFY || "");
      },
      undefined,
      newSignForm
    );
  };

  /**
   * Handles form submission and simulates registration process
   */
  const handleSubmitRegistration = useCallback(async () => {
    const userInfo: any = {}; // Object to hold user information

    // Check if form has elements
    for (let i = 0; i < form?.length; i++) {
      // Iterate over form elements
      const { name, value, isCountryCodeVisible, countryCode } = form[i] ?? {}; // Extract name and value from form element
      if (name && value) {
        if (isCountryCodeVisible) {
          // Check if name and value are present
          Object.assign(userInfo, {
            [name]: {
              number: value,
              countryCode,
            },
          }); // Add name-value pair to userInfo
        } else {
          // Check if name and value are present
          Object.assign(userInfo, { [name]: value }); // Add name-value pair to userInfo
        }
      }
    }
    const { firstName, lastName, phone, email } = userInfo;

    const value =
      phone?.number && !(userResp?.phone as any)?.isVerified
        ? { ...phone, number: phone.number.replace(/\D/g, "") }
        : undefined;

    const emailValue =
      email && !(userResp?.email as any)?.isVerified ? email : undefined;

    const resp = await submitReg(`${API_URL.USER}/${userResp._id}`, {
      firstName,
      lastName,
      phone: value,
      email: emailValue,
    });

    if (resp?.message?.toLowerCase().includes("exist")) {
      if (value && resp.message.toLowerCase().includes("phone")) {
        onErrorEvent({ message: "Phone already exist" });
      } else if (emailValue && resp.message.toLowerCase().includes("email")) {
        onErrorEvent({ message: "Email already exists" });
      } else {
        onErrorEvent({ message: "Phone or Email already exists" });
      }
      return;
    }

    setUserResp((prev) => ({ ...prev, ...resp }));

    if (resp?.phone?.isVerified === false) {
      sendMessage("signUp", { userId: resp._id });
      setCurrentModal("verify");
      setCurrentMethod("phone");
    } else if (resp?.email?.isVerified === false) {
      sendMessage("signUp", { userId: resp._id });
      setCurrentModal("verify");
      setCurrentMethod("email");
    } else if (instanceDetails.onboardingflow) {
      setCurrentModal(SIGNUP_SCREEN.ONBOARDING || "");
    } else {
      handleOnCompleteSignUpProcess();
    }
  }, [
    userResp,
    submitReg,
    setUserResp,
    instanceDetails.onboardingflow,
    form,
    onErrorEvent,
    sendMessage,
    setCurrentModal,
    setCurrentMethod,
    handleOnCompleteSignUpProcess,
  ]); // Dependency array to ensure the callback updates when form changes

  const handleResend = useCallback(async () => {
    const meta = {
      purpose:
        currentMethod === "phone" ? "phoneVerification" : "emailVerification",
    };

    const payload = {
      ...meta,
      userId: userResp._id,
    };
    const resp = await resend(API_URL.APPROVAL, payload);

    if (resp.statusCode !== 200) {
      onErrorEvent({
        type: "resend",
        ...resp,
      });
    } else {
      onStepChangeEvent({
        data: {
          ...resp,
          ...payload,
        },
        type: "resend",
      });
    }
  }, [currentMethod, userResp._id, resend, onStepChangeEvent, onErrorEvent]);

  const handleSocialSignUp = useCallback(
    async (el: { key: string }) => {
      setLoading(true);
      let url = `${API_URL.OAUTH}/${
        el.key
      }?instanceId=${instance_id}&type=signUp${
        env === ENVS.DEV ? "&test=true" : ""
      }`;
      if (el.key === "facebook" && facebook_redirect_url) {
        url = url + `&redirect_url=${facebook_redirect_url}`;
      }
      if (el.key === "microsoft" && microsoft_redirect_url) {
        url = url + `&redirect_url=${microsoft_redirect_url}`;
      }
      if (el.key === "google" && google_redirect_url) {
        url = url + `&redirect_url=${google_redirect_url}`;
      }
      if (el.key === "apple" && apple_redirect_url) {
        url = url + `&redirect_url=${apple_redirect_url}`;
      }

      //&platform=${webcomponentProps.platform || "web"}
      const resp = await socialAuth(url);
      if (resp?.statusCode === 200 && resp?.data?.url) {
        onStepChangeEvent({
          ...resp.data,
          type: "sso",
          from: "signUp",
          key: el.key,
        });
        popupRef = window.open(resp.data.url, "popup", "width=600,height=400");
      }
      setTimeout(() => {
        setLoading(false);
      }, 2000);
    },
    [
      apple_redirect_url,
      env,
      facebook_redirect_url,
      google_redirect_url,
      instance_id,
      microsoft_redirect_url,
      onStepChangeEvent,
      socialAuth,
    ]
  );

  useEffect(() => {
    // Register an event listener for the "approvalStatus" event on the getEvent object
    (getEvent as any)?.on?.("approvalStatus", (resp: any) => {
      // Check if the response status is "APPROVED"
      if (resp.status === "APPROVED") {
        // Create a shallow copy of userResp
        const newData: any = shallowCopy(userResp);

        // Determine the key to update based on the purpose of the response
        const key = resp.purpose === "emailVerification" ? "email" : "phone";

        // If the key exists in newData, mark it as verified
        if (newData[key]) {
          newData[key].isVerified = true;
        }

        const { email, firstName, lastName, phone } = newData;

        // Update the current step to the VERIFIED screen
        setCurrentModal(SIGNUP_SCREEN.VERIFIED);

        if (!instanceDetails.onboardingflow) {
          setTimeout(() => {
            setCurrentModal(undefined);
          }, 1000);
        }

        if (
          ((email?.isVerified && phone?.isVerified) ||
            instanceDetails.signUp?.scope.length === 1) &&
          firstName &&
          lastName
        ) {
          if (instanceDetails.onboardingflow) {
            setTimeout(() => {
              setCurrentModal(SIGNUP_SCREEN.ONBOARDING);
            }, 1000);
          } else {
            if (!isSignupCompleted) {
              isSignupCompleted = true;
              handleOnCompleteSignUpProcess();
            }
          }
        }

        // Check if both email and phone are verified or if there's only one scope in the sign-up process
        else if (
          (newData?.email?.isVerified && newData?.phone?.isVerified) ||
          instanceDetails.signUp?.scope.length === 1
        ) {
          // Update the userResp state to mark the current key as verified
          setUserResp((prev) => {
            const newObj = shallowCopy(prev); // Create a shallow copy of the previous state

            if (newObj[key]) {
              newObj[key].isVerified = true; // Mark the current key as verified
            }

            return newObj; // Return the updated state
          });

          // If conditions are met, set a timeout to update the current step to ACCOUNT_DETAILS after 1 second
          setTimeout(() => {
            setCurrentStep(SIGNUP_SCREEN.ACCOUNT_DETAILS);
          }, 1000);
        } else {
          if ((email || phone) && (!email?.isVerified || !phone?.isVerified)) {
            // If conditions are not met, set a timeout to update the current step to VERIFY and change the verification method
            setTimeout(() => {
              const pendingKey = key === "phone" ? "email" : "phone"; // Determine the pending key to verify
              setCurrentMethod(pendingKey); // Update the current verification method
              if (webcomponentProps?.iscombinedinput) {
                setForm((prev) =>
                  prev.map((field) =>
                    field.name === pendingKey
                      ? { ...field, disabled: false }
                      : field
                  )
                );
                setCurrentStep(SIGNUP_SCREEN.ACCOUNT_DETAILS); // Update the current step to VERIFY
              } else {
                setCurrentModal(SIGNUP_SCREEN.VERIFY); // Update the current step to VERIFY
              }
            }, 1000);
          }

          // Update the userResp state to mark the current key as verified
          setUserResp((prev) => {
            const newObj = shallowCopy(prev); // Create a shallow copy of the previous state

            if (newObj[key]) {
              newObj[key].isVerified = true; // Mark the current key as verified
            }

            return newObj; // Return the updated state
          });
        }
      } else if (resp.status === "REJECTED") {
        // If the response status is "REJECTED", update the current step to NOTVERIFIED
        setCurrentModal(SIGNUP_SCREEN.NOTVERIFIED);
      }
    });

    // Register an event listener for the "approvalStatus" event on the getEvent object
    (getEvent as any)?.on?.("signupCompleted", (resp: any) => {
      onSuccessEvent(resp);
    });

    // Specify getEvent as a dependency for the useEffect hook
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getEvent, userResp]);

  const handleCombinedInputChange = useCallback(
    (formData: any) => {
      console.log({ formData });
      setForm(() => {
        const newObj = shallowCopy(form) ?? form; // Create a shallow copy of the form state
        return newObj.map((el: any) => {
          if (el.name === formData.name) {
            return {
              ...el,
              value: formData.value,
              isHidden: false,
              ...(formData.name === "phone" && {
                countryCode: formData.countryCode,
              }),
            };
          } else {
            return {
              ...el,
              value: "",
              isHidden: false,
            };
          }
        });

        console.log({ newObj, formData, name: formData.name });
        return newObj; // Return updated form state
      });
    },
    [form, setForm]
  );

  return {
    submitCred, // Function for submitting credentials
    handleSubmitRegistration, // Function for handling registration submission
    form, // Form state
    handleChangeCountry, // Handler for changing country code
    handleChange, // Handler for form input changes
    setForm, // Setter function for updating form state
    setChecked, // Setter function for updating checked state
    checked, // State for whether terms and conditions checkbox is checked
    loadingRegistration, // State for loading status during registration
    loadingSubmitCredential, // State for loading status during submit credential
    currentStep, //State for currentStep
    setCurrentStep, // State for SetCurrentStep,
    handleResend, // Function for resend link  handleResend
    loadingResend, // State for loading status during resend
    handleOnCompleteSignUpProcess,
    handleSocialSignUp,
    currentMethod,
    handleCombinedInputChange,
    setCurrentModal,
    currentModal,
    manageDefaultUser,
    loading,
    handleSignUpSSO: handleSSO,
  };
};
